import React from 'react';

import {ANTIBODY_SCHEMA_INTERFACE_ID} from 'antibodies/schema-configuration/AntibodySchemaInterfaceConstants';
import BenchlingConfig from 'BenchlingConfig';
import BioentityImportOptions, {
  getEnabledBioentityImportOptions,
} from 'bulk-import/panes/BioentityImportOptions';
import EntitySchemaSSISelectionWithRestrictions from 'bulk-import/panes/EntitySchemaImplementingSSISelectionWithRestrictions';
import RegistryCollectionSelection from 'bulk-import/panes/RegistryCollectionSelection';
import * as SpreadsheetBulkImportActions from 'bulk-import/SpreadsheetBulkImportActions';
import {
  OPTIONS,
  RegistryImportSettingsProps,
  SelectedSchemaIds,
} from 'bulk-import/SpreadsheetBulkImportConstants';
import {BulkImportOptions, SelectedDestination} from 'bulk-import/SpreadsheetBulkImportStore';
import {RegistryLabelingStrategy, Registry} from 'constants/RegistryConstants';
import LabelingStrategyInput from 'registry/labeling-strategies/LabelingStrategyInput';
import ProjectFolderField from 'registry/ProjectFolderField';
import RegistrySelector from 'registry/RegistrySelector';
import {TRIAL_SAMPLE_SCHEMA_INTERFACE_ID} from 'registry/trial-interfaces/TrialInterfaceConstants';
import Col from 'ui-library/bs/Col';
import FormGroup from 'ui-library/bs/FormGroup';
import Row from 'ui-library/bs/Row';
import {ButtonStyle} from 'ui-library/buttons/Button';
import {LabelStyle} from 'ui-library/inputs/InputConstants';
import Modal from 'ui-library/modals/Modal';

import ModalButtonKey from '../../test/lib/cypress/shared/ModalButtonKey';

export interface ChooseImportIntoRegistryModalPaneProps extends RegistryImportSettingsProps {
  modalProps: {
    title: string;
    onRequestClose: () => void;
  };
  options: BulkImportOptions;
  selectedDestination: SelectedDestination | null;
  selectedSchemaIds: SelectedSchemaIds;
}

class ChooseImportIntoRegistryModalPane extends React.Component<ChooseImportIntoRegistryModalPaneProps> {
  private handleRegistryChange = (e: {target: {value: Registry | null}}): void => {
    const registry = e.target.value;
    SpreadsheetBulkImportActions.setImportSetting({registryImportSettings: {registry}});
  };

  private handleDestinationFolderChange = (e: {target: {value: string}}): void => {
    // `selectedDestination` is used for selecting either a folder OR registry in other flows. We re-use
    // this setting, but ignore `selectedDestination.registryId`, since the selected registry is stored
    // in `registryImportSettings.registry`.
    SpreadsheetBulkImportActions.setImportSetting({
      selectedDestination: {folderId: e.target.value, registryId: null},
    });
  };

  private handleLabelingStrategyChange = (labelingStrategy: RegistryLabelingStrategy): void => {
    SpreadsheetBulkImportActions.setImportSetting({registryImportSettings: {labelingStrategy}});
  };

  private isAtLeastOneCollectionSelected = (): boolean =>
    this.props.inOrgCollection || this.props.inUserCollection;

  override render(): JSX.Element {
    const folderId = this.props.selectedDestination ? this.props.selectedDestination.folderId : null;
    const folderIdRequired = BenchlingConfig.ENABLE_PROJECTS_REQUIRED_FOR_REGISTRY_FILES;
    return (
      <Modal
        enableRedesign={false}
        buttons={[
          {
            key: 'next-from-choose-spreadsheet-btn',
            text: 'Next',
            style: ButtonStyle.SECONDARY,
            onClick: SpreadsheetBulkImportActions.goToNextPane,
            disabled: !(
              this.props.registry &&
              this.props.selectedSchemaIds.tagSchemaId &&
              this.isAtLeastOneCollectionSelected() &&
              this.props.labelingStrategy &&
              (!folderIdRequired || folderId != null) &&
              (!this.props.options[OPTIONS.IMPORT_CONTAINERS] ||
                this.props.selectedSchemaIds.containerSchemaId)
            ),
            dataTestAttrs: {
              component: 'UploadSpreadsheetModal',
              element: 'footerButton',
              key: ModalButtonKey.NEXT,
            },
          },
        ]}
        {...this.props.modalProps}
      >
        <Row>
          <Col xs={6}>
            <div className="form-group">
              <label>Registry</label>
              <RegistrySelector
                onChange={this.handleRegistryChange}
                registryId={this.props.registry ? this.props.registry.id : null}
              />
            </div>
          </Col>
          <Col xs={6}>
            <EntitySchemaSSISelectionWithRestrictions
              label="Schema"
              registry={this.props.registry}
              onRequestClose={this.props.modalProps.onRequestClose}
              selectedSchemaId={this.props.selectedSchemaIds.tagSchemaId ?? null}
              excludedInterfaceIds={[TRIAL_SAMPLE_SCHEMA_INTERFACE_ID, ANTIBODY_SCHEMA_INTERFACE_ID]}
            />
          </Col>
        </Row>
        <RegistryCollectionSelection
          registry={this.props.registry}
          inOrgCollection={this.props.inOrgCollection}
          inUserCollection={this.props.inUserCollection}
        />
        <Row>
          <Col xs={12}>
            <FormGroup>
              <ProjectFolderField
                folderId={folderId}
                labelStyle={LabelStyle.LABEL}
                onChange={this.handleDestinationFolderChange}
                isRequired={BenchlingConfig.ENABLE_PROJECTS_REQUIRED_FOR_REGISTRY_FILES}
              />
              {this.props.selectedSchemaIds.tagSchemaId && (
                <div className="u-small-spacing-top">
                  <LabelingStrategyInput
                    labelingStrategy={this.props.labelingStrategy}
                    labelingStrategies={this.props.labelingStrategyOptions}
                    onChange={this.handleLabelingStrategyChange}
                    tagSchemaIds={[this.props.selectedSchemaIds.tagSchemaId]}
                  />
                </div>
              )}
            </FormGroup>
          </Col>
        </Row>
        <BioentityImportOptions
          enabledOptions={getEnabledBioentityImportOptions({
            options: this.props.options,
            selectedSchemaIds: this.props.selectedSchemaIds,
          })}
          options={this.props.options}
          selectedSchemaIds={this.props.selectedSchemaIds}
        />
      </Modal>
    );
  }
}

export default ChooseImportIntoRegistryModalPane;
